<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import {
  fetchMarketplaceJson,
  postMarketplaceJson,
  postMarketplaceJsonBody,
  patchMarketplaceJson,
} from 'dashboard/helpers/marketplaceApi';

const route = useRoute();
const accountId = route.params.accountId;

const loading = ref(false);
const errorMsg = ref('');
const okMsg = ref('');
const candidates = ref([]);
const approved = ref([]);
const rejected = ref([]);
const stats = ref({});
const tab = ref('aday');
const savingId = ref(null);
const scanning = ref(false);
const newPhone = ref('');
const newName = ref('');
const adding = ref(false);

const TABS = [
  { key: 'aday', label: 'Onay bekleyen' },
  { key: 'liste', label: 'Kara liste' },
  { key: 'elenen', label: 'Elenenler' },
];

const rows = computed(() => {
  if (tab.value === 'liste') return approved.value;
  if (tab.value === 'elenen') return rejected.value;
  return candidates.value;
});

const tabCount = key => {
  if (key === 'liste') return approved.value.length;
  if (key === 'elenen') return rejected.value.length;
  return candidates.value.length;
};

const fmtPhone = p =>
  p ? `0${p.slice(0, 3)} ${p.slice(3, 6)} ${p.slice(6, 8)} ${p.slice(8)}` : '';

const fmtDate = iso =>
  iso
    ? new Date(iso).toLocaleDateString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      })
    : '';

const rateCls = rate => {
  if (rate === 0)
    return 'bg-red-100 text-red-800 dark:bg-red-800/40 dark:text-red-200';
  if (rate <= 50)
    return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800/40 dark:text-yellow-200';
  return 'bg-green-100 text-green-800 dark:bg-green-800/40 dark:text-green-200';
};

const load = async () => {
  loading.value = true;
  errorMsg.value = '';
  try {
    const data = await fetchMarketplaceJson(accountId, 'cod_blacklists');
    candidates.value = data.candidates || [];
    approved.value = data.approved || [];
    rejected.value = data.rejected || [];
    stats.value = data.stats || {};
  } catch (e) {
    errorMsg.value = 'Liste yüklenemedi.';
  } finally {
    loading.value = false;
  }
};

const setStatus = async (entry, status) => {
  savingId.value = entry.id;
  errorMsg.value = '';
  okMsg.value = '';
  try {
    await patchMarketplaceJson(accountId, `cod_blacklists/${entry.id}`, {
      status,
    });
    okMsg.value =
      status === 'approved'
        ? `${fmtPhone(entry.phone)} kara listeye alındı — kapıda ödeme kapandı.`
        : `${fmtPhone(entry.phone)} listeden çıkarıldı — kapıda ödeme tekrar açık.`;
    await load();
  } catch (e) {
    errorMsg.value = 'Kaydedilemedi. Tekrar deneyin.';
  } finally {
    savingId.value = null;
  }
};

const removeFromList = entry => {
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      `${entry.customer_name || fmtPhone(entry.phone)} listeden çıkarılsın mı?\nBu numaraya kapıda ödeme tekrar açılacak.`
    )
  )
    return;
  setStatus(entry, 'rejected');
};

const addManual = async () => {
  const digits = newPhone.value.replace(/\D/g, '');
  if (digits.length < 10) {
    errorMsg.value = 'Telefon en az 10 haneli olmalı.';
    return;
  }
  adding.value = true;
  errorMsg.value = '';
  okMsg.value = '';
  try {
    await postMarketplaceJsonBody(accountId, 'cod_blacklists', {
      phone: digits,
      customer_name: newName.value,
    });
    okMsg.value = 'Numara kara listeye eklendi — kapıda ödeme kapandı.';
    newPhone.value = '';
    newName.value = '';
    await load();
  } catch (e) {
    errorMsg.value = e.message || 'Numara eklenemedi.';
  } finally {
    adding.value = false;
  }
};

const rescan = async () => {
  scanning.value = true;
  errorMsg.value = '';
  okMsg.value = '';
  try {
    const res = await postMarketplaceJson(accountId, 'cod_blacklists/rescan');
    okMsg.value = res.new_candidates
      ? `${res.new_candidates} yeni aday bulundu.`
      : 'Yeni aday çıkmadı, liste güncel.';
    await load();
  } catch (e) {
    errorMsg.value = 'Tarama yapılamadı.';
  } finally {
    scanning.value = false;
  }
};

onMounted(load);
</script>

<template>
  <div class="flex flex-col h-full overflow-auto bg-n-background">
    <div class="max-w-5xl w-full mx-auto px-6 py-6">
      <div class="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 class="text-2xl font-bold text-n-slate-12">
            Kapıda Ödeme Kara Listesi
          </h1>
          <p class="text-sm text-n-slate-11 mt-1">
            Kargosunu almayan müşteriler her gece otomatik bulunur. Siz
            onaylarsınız, o numaraya kapıda ödeme kasada anında kapanır.
          </p>
        </div>
        <button
          class="px-4 py-2 rounded-lg bg-woot-600 hover:bg-woot-700 text-white text-sm font-medium disabled:opacity-60"
          :disabled="scanning"
          @click="rescan"
        >
          {{ scanning ? 'Taranıyor…' : '🔄 Şimdi Tara' }}
        </button>
      </div>

      <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div class="rounded-lg border border-n-weak bg-n-solid-1 px-4 py-3">
          <div class="text-xs text-n-slate-11">Onay bekleyen</div>
          <div class="text-2xl font-bold text-n-slate-12">
            {{ stats.aday || 0 }}
          </div>
        </div>
        <div class="rounded-lg border border-n-weak bg-n-solid-1 px-4 py-3">
          <div class="text-xs text-n-slate-11">Kara listede</div>
          <div class="text-2xl font-bold text-n-slate-12">
            {{ stats.listede || 0 }}
          </div>
        </div>
        <div class="rounded-lg border border-n-weak bg-n-solid-1 px-4 py-3">
          <div class="text-xs text-n-slate-11">Mağazada geçerli</div>
          <div
            class="text-2xl font-bold"
            :class="
              stats.magazada === stats.listede
                ? 'text-green-700 dark:text-green-400'
                : 'text-red-700 dark:text-red-400'
            "
          >
            {{ stats.magazada == null ? '—' : stats.magazada }}
          </div>
        </div>
        <div class="rounded-lg border border-n-weak bg-n-solid-1 px-4 py-3">
          <div class="text-xs text-n-slate-11">Bekleyen risk</div>
          <div class="text-2xl font-bold text-n-slate-12">
            {{ (stats.risk_tl || 0).toLocaleString('tr-TR') }} TL
          </div>
        </div>
      </div>

      <div
        v-if="errorMsg"
        class="mb-4 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-800 dark:text-red-200"
      >
        {{ errorMsg }}
      </div>
      <div
        v-if="okMsg"
        class="mb-4 rounded-lg bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 px-4 py-3 text-sm text-green-800 dark:text-green-200"
      >
        {{ okMsg }}
      </div>

      <div class="flex gap-2 mb-4">
        <button
          v-for="t in TABS"
          :key="t.key"
          class="px-4 py-2 rounded-lg text-sm font-semibold border"
          :class="
            tab === t.key
              ? 'bg-woot-600 border-woot-700 text-white'
              : 'bg-n-solid-1 border-n-weak text-n-slate-11 hover:bg-n-solid-2'
          "
          @click="tab = t.key"
        >
          {{ t.label }} ({{ tabCount(t.key) }})
        </button>
      </div>

      <div v-if="loading" class="text-sm text-n-slate-11 py-8 text-center">
        Yükleniyor…
      </div>

      <div
        v-else-if="!rows.length"
        class="rounded-lg border border-n-weak bg-n-solid-1 px-4 py-10 text-center text-sm text-n-slate-11"
      >
        {{
          tab === 'aday'
            ? 'Onay bekleyen müşteri yok. Her şey temiz.'
            : 'Bu listede kayıt yok.'
        }}
      </div>

      <div v-else class="flex flex-col gap-3">
        <div
          v-for="row in rows"
          :key="row.id"
          class="rounded-lg border border-n-weak bg-n-solid-1 px-4 py-3 flex flex-wrap items-center justify-between gap-3"
        >
          <div class="min-w-[240px]">
            <div class="font-semibold text-n-slate-12">
              {{ row.customer_name || 'İsimsiz müşteri' }}
            </div>
            <div class="text-sm text-n-slate-11">{{ fmtPhone(row.phone) }}</div>
            <div class="text-xs text-n-slate-11 mt-1">
              Son sipariş: {{ row.last_order_on || '—' }}
              <span v-if="row.decided_by">
                · Karar: {{ row.decided_by }}
                {{ fmtDate(row.decided_at) }}</span
              >
            </div>
          </div>

          <div class="flex items-center gap-4 text-sm">
            <div class="text-center">
              <div class="text-lg font-bold text-red-700 dark:text-red-400">
                {{ row.undelivered }}
              </div>
              <div class="text-xs text-n-slate-11">almadığı</div>
            </div>
            <div class="text-center">
              <div class="text-lg font-bold text-n-slate-12">
                {{ row.shipped }}
              </div>
              <div class="text-xs text-n-slate-11">kargolu</div>
            </div>
            <span
              class="px-2 py-1 rounded text-xs font-semibold"
              :class="rateCls(row.rate)"
            >
              %{{ row.rate }} teslim aldı
            </span>
            <span v-if="row.risk_tl" class="text-xs text-n-slate-11">
              {{ row.risk_tl.toLocaleString('tr-TR') }} TL zarar
            </span>
          </div>

          <div class="flex items-center gap-2">
            <template v-if="row.status === 'candidate'">
              <button
                class="px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-60"
                :disabled="savingId === row.id"
                @click="setStatus(row, 'approved')"
              >
                Kara listeye al
              </button>
              <button
                class="px-3 py-2 rounded-lg border border-n-weak bg-n-solid-1 hover:bg-n-solid-2 text-n-slate-11 text-sm disabled:opacity-60"
                :disabled="savingId === row.id"
                @click="setStatus(row, 'rejected')"
              >
                Alma, iyi müşteri
              </button>
            </template>
            <template v-else-if="row.status === 'approved'">
              <span
                class="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-800/40 dark:text-red-200"
              >
                Kapıda ödeme KAPALI
              </span>
              <button
                class="px-3 py-2 rounded-lg border border-n-weak bg-n-solid-1 hover:bg-n-solid-2 text-n-slate-11 text-sm disabled:opacity-60"
                :disabled="savingId === row.id"
                @click="removeFromList(row)"
              >
                Listeden çıkar
              </button>
            </template>
            <template v-else>
              <button
                class="px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-60"
                :disabled="savingId === row.id"
                @click="setStatus(row, 'approved')"
              >
                Kara listeye al
              </button>
            </template>
          </div>
        </div>
      </div>

      <div class="mt-6 rounded-lg border border-n-weak bg-n-solid-1 px-4 py-4">
        <div class="text-sm font-semibold text-n-slate-12 mb-2">
          Elle numara ekle
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <input
            v-model="newPhone"
            type="tel"
            placeholder="0532 000 00 00"
            class="px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm w-48"
          />
          <input
            v-model="newName"
            type="text"
            placeholder="Müşteri adı (isteğe bağlı)"
            class="px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm w-64"
          />
          <button
            class="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-60"
            :disabled="adding"
            @click="addManual"
          >
            {{ adding ? 'Ekleniyor…' : 'Kara listeye ekle' }}
          </button>
        </div>
        <p class="text-xs text-n-slate-11 mt-2">
          Sadece modaxlarge.com kasasında geçerlidir. Koza (Ticimax) tarafı
          ayrıdır.
        </p>
      </div>
    </div>
  </div>
</template>
